/**
 * Onglet Réglages — **un sommaire et cinq sous-pages** (specs §6.8).
 *
 * 🔴 Demandé après usage. Les cinq volets — thème, planning, objectif de poids,
 * notifications, compte — vivaient l'un sous l'autre sur une seule page très
 * longue : régler une heure de notification demandait de faire défiler sept
 * lignes de planning, et rien ne disait ce que la page contenait avant d'y être
 * descendu.
 *
 * ⚠️ **Un sommaire, pas des onglets** — contrairement à la Bibliothèque. Deux
 * raisons, et la seconde suffirait :
 *
 *   1. **Cinq libellés ne tiennent pas** en une rangée sur 393 px. Trois, oui —
 *      c'est pourquoi la Bibliothèque, elle, a des onglets.
 *   2. Des onglets supposent des contenus **de même nature** entre lesquels on
 *      circule. Ici « Thème » et « Compte » n'ont rien à voir : on entre dans
 *      l'un, on en ressort, on n'y navigue pas latéralement.
 *
 * 🔴 **Les sous-pages sont de vraies routes.** C'est ce qui rend le retour par
 * glissement natif (§6.1) opérant — on remonte au sommaire sans qu'on ait rien
 * écrit — et ce qui permet à la mémoire d'onglet (`lib/tabMemory.ts`) de
 * ramener sur le volet qu'on consultait.
 *
 * ⚠️ ARBITRAGE Bibliothèque / Réglages (specs §6.8 contre §8.2) : le *planning*
 * est ici parce qu'il règle un comportement, les *modèles* eux-mêmes sont dans
 * la Bibliothèque parce que ce sont des objets du référentiel. Raisonnement
 * complet en tête de `screens/library/TemplatesSection.tsx`.
 *
 * ⚠️ **Le §6.8 est complet depuis l'étape 10** : thème, planning, objectif de
 * poids (par renvoi), notifications, compte. Rien n'y reste ouvert.
 *
 * ⚠️ **Un cinquième volet s'y ajoute en V2 : « Synchronisation »** (§3.5). Il ne
 * relève pas du §6.8 — ce n'est pas une préférence mais un **diagnostic**, celui
 * que le §3.3 exige : « une opération en échec n'est jamais supprimée en
 * silence, elle reste visible avec son motif ». Il fallait un endroit pour la
 * voir, et les Réglages sont le seul écran dont c'est le rôle.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * REFONTE « NOURRI » — ce qui a changé, et ce qui n'a pas bougé
 * ══════════════════════════════════════════════════════════════════════════
 *
 * L'habillage suit désormais l'écran « Réglages » du prototype : une seule
 * `Card` sans rembourrage, des lignes séparées par un filet, chacune ouverte
 * par une pastille teintée. Le sommaire, les cinq volets, leurs routes, le
 * repli sur section inconnue et le renvoi vers Progression sont **inchangés**.
 *
 * ⚠️ **Les sous-pages elles-mêmes ne sont pas migrées** (thème, planning,
 * notifications, compte, synchronisation) : elles lisent les anciens jetons,
 * que le pont de `tokens-nourri.css` tient au clair. C'est la même méthode
 * qu'à l'étape 2 — une surface à la fois, jamais toutes d'un coup.
 */

import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';

import {
  Card,
  GlyphBell,
  GlyphBolt,
  GlyphCalendar,
  GlyphChevronLeft,
  GlyphChevronRight,
  GlyphSync,
  GlyphTheme,
  GlyphTrend,
  GlyphUser,
} from '../../components/nourri';
import { ScreenTitle } from '../../components/ui/ScreenHeader';
import { AccountSection } from './AccountSection';
import { IntervalsSection } from './IntervalsSection';
import { NotificationsSection } from './NotificationsSection';
import { PlanningSection } from './PlanningSection';
import { SyncSection } from './SyncSection';
import { ThemeSection } from './ThemeSection';

/* ==========================================================================
 * Sommaire
 * ======================================================================= */

interface Volet {
  section: string;
  titre: string;
  resume: string;
  /**
   * ⚠️ Le glyphe est **décoratif** : il est `aria-hidden`, et c'est le titre
   * qui nomme la ligne. Une pastille ne dit pas « Notifications » à qui ne la
   * connaît pas — elle aide seulement à retrouver une ligne déjà connue.
   */
  glyphe: ReactNode;
  rendu: () => ReactNode;
}

const VOLETS: readonly Volet[] = [
  {
    section: 'theme',
    titre: 'Thème',
    resume: 'Clair, sombre ou celui de l’appareil.',
    glyphe: <GlyphTheme />,
    rendu: () => <ThemeSection />,
  },
  {
    section: 'planning',
    titre: 'Planning',
    resume: 'Quel modèle de journée s’applique quel jour.',
    glyphe: <GlyphCalendar />,
    rendu: () => <PlanningSection />,
  },
  {
    section: 'notifications',
    titre: 'Notifications',
    resume: 'Rappel de pesée, journal vide, bilan du soir.',
    glyphe: <GlyphBell />,
    rendu: () => <NotificationsSection />,
  },
  {
    section: 'compte',
    titre: 'Compte',
    resume: 'Passkeys, clé de secours, déconnexion.',
    glyphe: <GlyphUser />,
    rendu: () => <AccountSection />,
  },
  /*
   * ⚠️ **En dernier, et c'est voulu.** C'est un volet de diagnostic : on ne
   * l'ouvre pas pour régler quelque chose mais pour comprendre pourquoi une
   * saisie n'est pas arrivée. La pastille de l'en-tête y mène directement quand
   * il y a effectivement quelque chose à voir (V2 §3.5), ce qui est le chemin
   * normal — cette ligne-ci est celle qu'on emprunte quand on cherche sans
   * qu'on nous ait rien signalé.
   */
  {
    section: 'synchronisation',
    titre: 'Synchronisation',
    resume: 'Saisies faites sans réseau, et ce qui reste à envoyer.',
    glyphe: <GlyphSync />,
    rendu: () => <SyncSection />,
  },
  /*
   * ⚠️ **Distinct de « Synchronisation », et juste après lui.** Les deux
   * portent le même mot et n'ont rien à voir : celui du dessus est la file des
   * saisies faites hors ligne (V2 §3.3), celui-ci l'ingestion **entrante** d'un
   * tiers (§5.2). Les fondre ferait chercher longtemps pourquoi « 3 en
   * attente » ne parle pas de vélo — d'où deux lignes, et deux libellés qui ne
   * se confondent pas.
   *
   * ⚠️ **Un état, pas un réglage** : il n'y a rien à saisir, la clé étant un
   * secret serveur. Il vit quand même ici parce que c'est le seul écran dont le
   * rôle est de répondre à « est-ce que ça marche, et sinon pourquoi ».
   */
  {
    section: 'activite',
    titre: 'Activité (Coros)',
    resume: 'État de l’import intervals.icu — indicatif, sans effet sur les objectifs.',
    glyphe: <GlyphBolt size={19} />,
    rendu: () => <IntervalsSection />,
  },
];

/**
 * Ligne du sommaire — prototype `.settings-row`.
 *
 * ⚠️ Un `<Link>` et non un bouton : c'est une navigation, et le chevron le dit.
 * La cible tactile fait toute la largeur — sur un écran de réglages, viser le
 * chevron seul serait un piège.
 */
function SommaireRow({
  to,
  titre,
  resume,
  glyphe,
}: {
  to: string;
  titre: string;
  resume: string;
  glyphe: ReactNode;
}) {
  return (
    <Link to={to} className="n-settings-row">
      <span className="n-settings-icon" aria-hidden="true">
        {glyphe}
      </span>
      <span className="n-settings-row__main">
        <span className="n-settings-row__title">{titre}</span>
        <span className="n-settings-row__sub">{resume}</span>
      </span>
      <span className="n-settings-row__chevron" aria-hidden="true">
        <GlyphChevronRight />
      </span>
    </Link>
  );
}

/* ==========================================================================
 * Écran
 * ======================================================================= */

export function SettingsScreen() {
  const { section } = useParams<{ section?: string }>();
  const volet = VOLETS.find((v) => v.section === section);

  /*
   * Sommaire — également le repli d'une section inconnue.
   * ⚠️ `/reglages/nimportequoi` montre donc le sommaire plutôt qu'une page
   * vide : une adresse de l'application affiche toujours quelque chose.
   */
  if (!volet) {
    return (
      <section className="n-page">
        <ScreenTitle>Réglages</ScreenTitle>

        {/*
          ⚠️ `padding="none"` : les lignes touchent les deux bords de la carte,
          sinon ni leurs filets ni leur fond au contact n'iraient jusqu'au
          rayon de 22 px. C'est la `.list-card` du prototype.
        */}
        <Card padding="none">
          {VOLETS.map((v) => (
            <SommaireRow
              key={v.section}
              to={`/reglages/${v.section}`}
              titre={v.titre}
              resume={v.resume}
              glyphe={v.glyphe}
            />
          ))}

          {/*
            Objectif de poids (specs §6.8, étape 8).

            ⚠️ Il reste un **renvoi** et ne devient pas une sous-page :
            l'objectif ne se lit qu'à côté de ce qu'il mesure — l'écart entre le
            rythme visé et le rythme réel. Le formulaire vit donc dans
            Progression. Même arbitrage qu'entre les modèles et le planning.

            ⚠️ Mais c'est un renvoi **qui mène quelque part** : une explication
            seule, en bas d'une liste de lignes cliquables, se lit comme un
            reliquat et oblige à retrouver l'onglet soi-même. La ligne emmène
            donc dans Progression, et son résumé dit pourquoi le réglage est
            là-bas.

            ⚠️ **Dans la même carte que les cinq volets**, à la différence de la
            version d'avant où elle vivait à part. Le prototype ne connaît
            qu'une liste ; et une ligne isolée sous la carte se serait lue comme
            une septième option d'un autre rang, ce qu'elle n'est pas — c'est
            une entrée de sommaire comme les autres, qui mène simplement dans un
            autre onglet.
          */}
          <SommaireRow
            to="/progression"
            titre="Objectif de poids"
            resume="Sous le graphique de Progression, à côté de l’écart qu’il mesure."
            glyphe={<GlyphTrend />}
          />
        </Card>
      </section>
    );
  }

  return (
    <section className="n-page">
      {/*
        ⚠️ Le titre de l'en-tête devient celui du volet, et un lien de retour
        s'y ajoute. Le geste de glissement natif fait la même chose, mais il
        n'existe pas sur l'ordinateur — et un écran sans issue visible est un
        écran où l'on se sent coincé.
      */}
      <ScreenTitle>
        <span className="n-row">
          {/*
            ⚠️ Cible de 44 px, avec une marge négative pour que le titre reste
            à sa place : une flèche de retour est le contrôle le plus souvent
            visé de l'écran, et c'est le glyphe le plus petit.
          */}
          <Link to="/reglages" aria-label="Retour aux réglages" className="n-back">
            <GlyphChevronLeft />
          </Link>
          {volet.titre}
        </span>
      </ScreenTitle>

      {volet.rendu()}
    </section>
  );
}
